import { useCallback, useState } from "react";
import { AxiosError } from "axios";
import { QuestionsChallengeActionError } from "@interfaces";
import {
  ENROLMENT_STEP,
  ENROLMENT_STEP_STATUS,
  InlineError,
  useEnrolmentStatusContext,
} from "@contexts/EnrolmentStatusContext";
import { ActionBlockModal } from "../components/ActionBlockModal";
import { ActionRetryModal } from "../components/ActionRetryModal";
import { ErrorType, getEnrolmentErrorDisplay } from "./onQuestionChallengeError";

type ModalContent = {
  title: string;
  description?: string | React.ReactNode;
  nextStep?: ENROLMENT_STEP;
  nextStepStatus?: ENROLMENT_STEP_STATUS;
};

type ActionErrorHandler = (error: InlineError) => boolean;

// Responses that carry no `action` still route the doctor somewhere; resolve
// their copy against the step they land on instead of the generic default.
const FALLBACK_ERROR_NAME_BY_STEP: Partial<Record<ENROLMENT_STEP, string>> = {
  [ENROLMENT_STEP.CI_DOC_NUM]: "documentNumberMismatch",
};

type Props = {
  onRetry?: () => void;
  onInvalidInput?: ActionErrorHandler;
  onIncompleteData?: ActionErrorHandler;
  fallbackTitle?: string;
};

export const useEnrolmentErrorHandler = ({
  onRetry,
  onInvalidInput,
  onIncompleteData,
  fallbackTitle = "Ha ocurrido un error",
}: Props = {}) => {
  const {
    currentStep,
    getEnrolmentStatus,
    setCurrentStep,
    setCurrentStepStatus,
    setPendingInlineError,
  } = useEnrolmentStatusContext();

  const [blockModal, setBlockModal] = useState<ModalContent | null>(null);
  const [retryModal, setRetryModal] = useState<ModalContent | null>(null);

  const applyNextStep = useCallback(
    async (nextStep?: ENROLMENT_STEP, nextStepStatus?: ENROLMENT_STEP_STATUS) => {
      if (nextStep && nextStepStatus) {
        setCurrentStep(nextStep);
        setCurrentStepStatus(nextStepStatus);
        return;
      }
      await getEnrolmentStatus();
    },
    [getEnrolmentStatus, setCurrentStep, setCurrentStepStatus],
  );

  // For a handed-over error the destination screen could not place on one of
  // its inputs: better a modal than nothing.
  const showErrorAsModal = useCallback(
    ({ name, description }: InlineError) =>
      setRetryModal({ title: name, description }),
    [],
  );

  const handleActionError = useCallback(
    async (error: QuestionsChallengeActionError) => {
      const content = {
        title: error.name,
        description: error.description,
        nextStep: error.nextStep,
        nextStepStatus: error.nextStepStatus,
      };
      switch (error.action) {
        case "block":
          setBlockModal(content);
          return;
        case "invalidInput":
          if (!onInvalidInput?.(error)) {
            // The doctor is about to be moved to the step this error belongs
            // to, and this screen unmounts with it: hand the error over
            // instead of opening a modal that dies with us.
            if (error.nextStep && error.nextStep !== currentStep) {
              setPendingInlineError({
                name: error.name,
                description: error.description,
                nextStep: error.nextStep,
              });
            } else {
              setRetryModal(content);
            }
          }
          await applyNextStep(error.nextStep, error.nextStepStatus);
          return;
        case "retry":
          setRetryModal(content);
          await applyNextStep(error.nextStep, error.nextStepStatus);
          return;
        case "incompleteData":
          if (!onIncompleteData?.(error)) setRetryModal(content);
          await applyNextStep(error.nextStep, error.nextStepStatus);
          return;
      }
    },
    [
      applyNextStep,
      currentStep,
      onInvalidInput,
      onIncompleteData,
      setPendingInlineError,
    ],
  );

  const handleEnrolmentError = useCallback(
    async (error: Error) => {
      console.error("Enrolment error: ", error);
      if (error instanceof QuestionsChallengeActionError) {
        await handleActionError(error);
        return;
      }
      const errorData = (error as AxiosError<ErrorType>).response?.data;
      const nextStep = errorData?.nextStep;
      const { name, description } = getEnrolmentErrorDisplay(
        error,
        nextStep && FALLBACK_ERROR_NAME_BY_STEP[nextStep],
      );
      const handledInline = onInvalidInput?.({ name, description, nextStep });
      if (!handledInline) {
        setRetryModal({
          title: fallbackTitle,
          description: description ?? name,
        });
      }
      await applyNextStep(nextStep, errorData?.nextStepStatus);
    },
    [applyNextStep, fallbackTitle, handleActionError, onInvalidInput],
  );

  const enrolmentErrorModals = (
    <>
      <ActionBlockModal
        isOpen={!!blockModal}
        onClose={() => setBlockModal(null)}
        title={blockModal?.title ?? ""}
        description={blockModal?.description}
        nextStep={blockModal?.nextStep}
        nextStepStatus={blockModal?.nextStepStatus}
      />
      <ActionRetryModal
        isOpen={!!retryModal}
        onClose={() => setRetryModal(null)}
        title={retryModal?.title ?? ""}
        description={retryModal?.description}
        onRetry={onRetry}
      />
    </>
  );

  return { handleEnrolmentError, showErrorAsModal, enrolmentErrorModals };
};
